import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, CATEGORIES, fmtDuration, fmtBytes } from "@/lib/api";
import { Upload, Film, Scissors, ArrowRight, Brain, Link2 } from "lucide-react";
import UrlFetchPanel from "@/components/UrlFetchPanel";

const CAT_IMAGES = {
  barrel_racing: "https://images.unsplash.com/photo-1760041870203-db13f8b0e664?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxob3JzZSUyMHJvZGVvJTIwYWN0aW9ufGVufDB8fHx8MTc3ODI0OTU3NXww&ixlib=rb-4.1.0&q=85",
  team_roping: "https://images.unsplash.com/photo-1741665196223-4457dd8c6680?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxob3JzZSUyMHJvZGVvJTIwYWN0aW9ufGVufDB8fHx8MTc3ODI0OTU3NXww&ixlib=rb-4.1.0&q=85",
  tie_down_roping: "https://images.unsplash.com/photo-1578796232543-5e74775b6e86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxiYXJyZWwlMjByYWNpbmclMjByb2Rlb3xlbnwwfHx8fDE3NzgyNDk1NzV8MA&ixlib=rb-4.1.0&q=85",
  steer_wrestling: "https://images.unsplash.com/photo-1741665196223-4457dd8c6680?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxob3JzZSUyMHJvZGVvJTIwYWN0aW9ufGVufDB8fHx8MTc3ODI0OTU3NXww&ixlib=rb-4.1.0&q=85",
  breakaway: "https://images.unsplash.com/photo-1760041870271-496f3d0d46ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxob3JzZSUyMHJvZGVvJTIwYWN0aW9ufGVufDB8fHx8MTc3ODI0OTU3NXww&ixlib=rb-4.1.0&q=85",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [videos, setVideos] = useState([]);
  const [totalClips, setTotalClips] = useState(0);
  const [training, setTraining] = useState([]);
  const [showUrlFetch, setShowUrlFetch] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/videos"),
      api.get("/clips"),
      api.get("/training/stats"),
    ]).then(([cats, vids, clips, train]) => {
      setStats(cats.data.categories);
      setVideos(vids.data);
      setTotalClips(clips.data.length);
      setTraining(train.data.categories);
    }).catch((e) => console.error(e));
  }, []);

  const totalVideos = videos.length;
  const totalDuration = videos.reduce((a, v) => a + (v.duration || 0), 0);
  const totalSize = videos.reduce((a, v) => a + (v.size_bytes || 0), 0);

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1400px]" data-testid="dashboard-page">
      {/* Hero */}
      <div className="border-l-4 border-orange-600 pl-5 mb-12">
        <div className="label-tiny mb-2">Control Room</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl font-bold leading-[0.95]" data-testid="dashboard-title">
          Cut every run.<br />
          <span className="text-orange-600">Build the library.</span>
        </h1>
        <p className="text-stone-400 mt-4 max-w-xl">
          Upload rodeo footage. Mark in/out points (or let Gemini suggest them). Generate clean clips by event — barrel racing, team roping, tie-down, steer wrestling, breakaway.
        </p>
        <div className="flex gap-3 mt-6 flex-wrap">
          <Link to="/studio" className="btn-primary inline-flex items-center gap-2" data-testid="cta-new-cut">
            <Upload size={14} /> New Cut Session
          </Link>
          <button
            onClick={() => setShowUrlFetch((v) => !v)}
            className="btn-secondary inline-flex items-center gap-2 hover:!border-orange-700 hover:!text-orange-400"
            data-testid="cta-fetch-url"
          >
            <Link2 size={14} /> Fetch from URL
          </button>
          <Link to="/library" className="btn-secondary inline-flex items-center gap-2" data-testid="cta-view-library">
            <Film size={14} /> View Library
          </Link>
        </div>

        {showUrlFetch && (
          <div className="mt-5 max-w-2xl border border-stone-800 bg-stone-950/40 p-4" data-testid="dashboard-url-fetch">
            <UrlFetchPanel
              compact
              onComplete={(id) => navigate(`/studio/${id}`)}
            />
          </div>
        )}
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-800 mb-12 border border-stone-800" data-testid="dashboard-stats">
        <Stat label="Source Videos" value={totalVideos} />
        <Stat label="Clips Cut" value={totalClips} accent />
        <Stat label="Footage" value={fmtDuration(totalDuration)} />
        <Stat label="On Disk" value={fmtBytes(totalSize)} />
      </div>

      {/* Training panel */}
      <div className="mb-12">
        <div className="flex items-end justify-between mb-5 border-b border-stone-800 pb-3">
          <div>
            <h2 className="font-display uppercase text-2xl font-bold tracking-tight flex items-center gap-2">
              <Brain size={20} className="text-amber-500" /> AI Training Progress
            </h2>
            <p className="text-stone-500 text-xs mt-1">Each thumbs up/down on cuts and clips teaches Gemini your team's standards.</p>
          </div>
          <span className="label-tiny">few-shot learning</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-stone-800 border border-stone-800" data-testid="training-grid">
          {CATEGORIES.map((cat) => {
            const t = training.find((s) => s.category === cat.id) || { good: 0, bad: 0, manual: 0 };
            const total = t.good + t.manual;
            const score = Math.min(100, Math.round((total / 30) * 100));
            return (
              <div key={cat.id} className="bg-stone-950 p-4" data-testid={`training-${cat.id}`}>
                <div className="label-tiny mb-2">{cat.name}</div>
                <div className="flex items-baseline gap-3 font-mono-tc">
                  <span className="text-emerald-400 text-2xl font-bold">{t.good + t.manual}</span>
                  <span className="text-stone-600 text-xs">good</span>
                  {t.bad > 0 && (<>
                    <span className="text-rose-400 text-base">{t.bad}</span>
                    <span className="text-stone-600 text-xs">bad</span>
                  </>)}
                </div>
                <div className="mt-2 h-1 bg-stone-800">
                  <div className="h-full bg-gradient-to-r from-amber-600 to-emerald-500" style={{ width: `${score}%` }} />
                </div>
                <div className="text-[10px] text-stone-600 uppercase tracking-wider mt-1">
                  {score < 33 ? "learning" : score < 66 ? "improving" : score < 100 ? "trained" : "well-trained"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Categories */}
      <div className="mb-12">
        <div className="flex items-end justify-between mb-5 border-b border-stone-800 pb-3">
          <h2 className="font-display uppercase text-2xl font-bold tracking-tight">Event Categories</h2>
          <span className="label-tiny">{CATEGORIES.length} disciplines</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="category-grid">
          {CATEGORIES.map((cat) => {
            const stat = stats.find((s) => s.id === cat.id) || { clip_count: 0, video_count: 0 };
            const bgImage = CAT_IMAGES[cat.id];
            return (
              <Link
                key={cat.id}
                to={`/library?category=${cat.id}`}
                className="group relative overflow-hidden border border-stone-800 hover:border-orange-700 transition-colors aspect-[4/5] block"
                data-testid={`category-card-${cat.id}`}
              >
                {bgImage ? (
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-40 group-hover:opacity-60 transition-opacity"
                    style={{ backgroundImage: `url(${bgImage})` }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-900/40 via-stone-900 to-stone-950 opacity-70 group-hover:opacity-90 transition-opacity" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/70 to-transparent" />
                <div className="relative h-full p-4 flex flex-col justify-between">
                  <div className="text-orange-500 font-mono-tc text-xs">{String(cat.id.charCodeAt(0) + cat.id.length).padStart(2, "0")} /</div>
                  <div>
                    <div className="font-display uppercase font-bold text-2xl leading-tight">{cat.name}</div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-700/60">
                      <span className="text-xs text-stone-400">
                        <span className="font-mono-tc text-orange-500 text-base">{stat.clip_count}</span> clips
                      </span>
                      <ArrowRight size={14} className="text-stone-500 group-hover:text-orange-500 transition-colors" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent videos */}
      <div>
        <div className="flex items-end justify-between mb-5 border-b border-stone-800 pb-3">
          <h2 className="font-display uppercase text-2xl font-bold tracking-tight">Recent Source Footage</h2>
          <Link to="/studio" className="label-tiny hover:text-orange-500" data-testid="upload-link">+ Upload</Link>
        </div>
        {videos.length === 0 ? (
          <div className="border border-dashed border-stone-800 p-10 text-center" data-testid="no-videos">
            <Scissors size={28} className="mx-auto text-stone-600 mb-3" />
            <p className="text-stone-400">No source videos yet.</p>
            <Link to="/studio" className="btn-primary mt-4 inline-block" data-testid="empty-upload-cta">Upload First Video</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {videos.slice(0, 6).map((v) => (
              <Link
                key={v.id}
                to={`/studio/${v.id}`}
                className="border border-stone-800 hover:border-orange-700 p-4 transition-colors block"
                data-testid={`video-card-${v.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display uppercase font-semibold text-lg leading-tight truncate flex-1">
                    {v.original_name}
                  </div>
                </div>
                <div className="flex gap-3 text-xs text-stone-500 font-mono-tc">
                  <span>{fmtDuration(v.duration)}</span>
                  <span>·</span>
                  <span>{fmtBytes(v.size_bytes)}</span>
                  {v.category && (
                    <>
                      <span>·</span>
                      <span className="text-orange-500 uppercase">{v.category.replace(/_/g, " ")}</span>
                    </>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-stone-950 p-5">
      <div className="label-tiny mb-2">{label}</div>
      <div className={`font-display font-bold text-4xl leading-none ${accent ? "text-orange-500" : "text-stone-100"}`}>
        {value}
      </div>
    </div>
  );
}
